import type { ReactNode } from "react";
import { UserIcon } from "lucide-react";

import { relative } from "@/components/tools/assistant/format";
import { Chip } from "@/components/tools/assistant/shared";
import {
  accentFor,
  ASK_STATUS,
  ITEM_STATUS,
  itemStatus,
  RESOLUTION_KIND,
  URGENCY,
} from "@/components/tools/assistant/vocab";

import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { AssistantProactiveItem } from "@/types";

/**
 * One surfaced customer email: who, how urgent, where it stands, and each ask
 * with how it was settled. Used in the conversation view and the Items tab.
 */
export const ItemCard = ({
  item,
  header,
  footer,
  compact = false,
}: {
  item: AssistantProactiveItem;
  // Extra chips on the title row (e.g. the Staffer's name).
  header?: ReactNode;
  // Rendered under the asks (e.g. the delivery timeline).
  footer?: ReactNode;
  compact?: boolean;
}) => {
  const status = itemStatus(item);
  const snoozed =
    item.status === "open" &&
    !!item.snoozedUntil &&
    new Date(item.snoozedUntil).getTime() > Date.now();
  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-lg border border-l-4 bg-white",
        accentFor(status.tone),
        compact ? "p-2.5" : "p-4",
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {header}
        <span className={cn("font-medium text-slate-900", compact ? "text-sm" : "text-base")}>
          {item.subject || "(no subject)"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip
          def={status}
          detail={item.resolvedAt ? relative(item.resolvedAt) : undefined}
        />
        {snoozed && item.snoozedUntil && (
          <Chip
            def={{
              ...ITEM_STATUS.snoozed,
              label: `Snoozed until ${dates.formatShortDateTime(item.snoozedUntil)}`,
            }}
          />
        )}
        <Chip def={URGENCY[item.urgency] ?? URGENCY.no_deadline} />
        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
          <UserIcon className="size-3.5" />
          {item.customer.name}
          {item.customer.company ? (
            <span className="font-medium text-slate-800">
              · {item.customer.company}
            </span>
          ) : null}
          <span className="text-slate-400">
            {item.kind === "quiet_customer"
              ? `· waiting on them since ${relative(item.sentAt)}${item.followUpsSent ? ` · ${item.followUpsSent.toString()} follow-up${item.followUpsSent === 1 ? "" : "s"} unanswered` : ""}`
              : `· emailed ${relative(item.sentAt)}`}
          </span>
        </span>
      </div>
      {item.reason && (
        <p className="rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-700">
          {item.reason}
        </p>
      )}
      {item.asks.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {item.asks.map((ask, i) => (
            <li
              className="flex flex-wrap items-center gap-1.5 text-xs"
              key={`${ask.ask}-${i.toString()}`}
            >
              <Chip def={ASK_STATUS[ask.status]} />
              <span className="text-slate-800">{ask.ask}</span>
              {ask.candidate && (
                <span className="text-slate-500">({ask.candidate})</span>
              )}
              {ask.resolution && (
                <Chip
                  def={RESOLUTION_KIND[ask.resolution.kind] ?? RESOLUTION_KIND.other}
                  detail={ask.resolution.detail}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {footer}
    </div>
  );
};
